'use client'

import { useT } from '@/i18n/client'
import { Item } from '@/models/Form'
import { acceptedRule, emailRule, requiredRule } from '@/utils/rules'
import { Col, Form, FormItemProps } from 'antd'
import { Rule } from 'antd/lib/form'
import dayjs, { Dayjs } from 'dayjs'
import { FC, PropsWithChildren, useMemo } from 'react'
import { SBDynamicFormInputBirthDate } from '../inputs/SBDynamicFormInputBirthDate'
import { SBDynamicFormInputCheckbox } from '../inputs/SBDynamicFormInputCheckbox'
import { SBDynamicFormInputEmail } from '../inputs/SBDynamicFormInputEmail'
import { SBDynamicFormInputFirstName } from '../inputs/SBDynamicFormInputFirstName'
import { SBDynamicFormInputLastName } from '../inputs/SBDynamicFormInputLastName'
import { SBDynamicFormInputOptin } from '../inputs/SBDynamicFormInputOptin'
import { SBDynamicFormInputPhone } from '../inputs/SBDynamicFormInputPhone'
import { SBDynamicFormInputPhoto } from '../inputs/SBDynamicFormInputPhoto'
import { SBDynamicFormInputPlan } from '../inputs/SBDynamicFormInputPlan'
import { SBDynamicFormInputRadio } from '../inputs/SBDynamicFormInputRadio'
import { SBDynamicFormInputSegment } from '../inputs/SBDynamicFormInputSegment'
import { SBDynamicFormInputSelect } from '../inputs/SBDynamicFormInputSelect'
import { SBDynamicFormInputText } from '../inputs/SBDynamicFormInputText'
import { SBDynamicFormInputTextarea } from '../inputs/SBDynamicFormInputTextarea'
import { SBDynamicFormInput } from '../inputs/types'

const ColWrapper: FC<PropsWithChildren<{ item: Item }>> = ({
  item,
  children,
}) => {
  const colSpan = JSON.parse(item.colSpan) as {
    xs: number
    sm: number
    md: number
  }
  return (
    <Col key={item.id} xs={colSpan.xs} sm={colSpan.sm} md={colSpan.md}>
      {children}
    </Col>
  )
}

type Props = {
  item: Item
  pathPrefix: string[]
}

const SBDynamicFormItem = ({ item, pathPrefix }: Props) => {
  const { t } = useT('form')
  const form = Form.useFormInstance()
  const dependenceWatch = Form.useWatch(
    [...pathPrefix, item.conditionedByFormItemName || ''],
    form
  )

  const shouldHideItem = useMemo(() => {
    if (!item.conditionedByFormItemName) return false
    if (!dependenceWatch) return true
    const dependenceValues = Array.isArray(dependenceWatch)
      ? dependenceWatch
      : [dependenceWatch]
    return !dependenceValues.some((dependence: string) =>
      item.conditionedByFormItemOptions.includes(Number(dependence))
    )
  }, [
    item.conditionedByFormItemName,
    item.conditionedByFormItemOptions,
    dependenceWatch,
  ])

  if (!item || shouldHideItem) return <></>

  const inputMap: Record<
    string,
    {
      component: SBDynamicFormInput
      initialValue?: number
      rules?: FormItemProps['rules']
      valuePropName?: FormItemProps['valuePropName']
      getValueProps?: FormItemProps['getValueProps']
      getValueFromEvent?: FormItemProps['getValueFromEvent']
      hideLabel?: boolean
    }
  > = {
    first_name: { component: SBDynamicFormInputFirstName },
    last_name: { component: SBDynamicFormInputLastName },
    text: { component: SBDynamicFormInputText },
    textarea: { component: SBDynamicFormInputTextarea },
    email: { component: SBDynamicFormInputEmail, rules: [emailRule(t)] },
    phone: { component: SBDynamicFormInputPhone },
    birthDate: {
      component: SBDynamicFormInputBirthDate,
      getValueProps: (value) => ({ value: value ? dayjs(value) : undefined }),
      getValueFromEvent: (date: Dayjs) =>
        date ? date.format('YYYY/MM/DD') : null,
    },
    radio: { component: SBDynamicFormInputRadio },
    checkbox: { component: SBDynamicFormInputCheckbox },
    select: { component: SBDynamicFormInputSelect },
    plan: { component: SBDynamicFormInputPlan },
    segment: {
      component: SBDynamicFormInputSegment,
      initialValue: item.options?.[0]?.id,
    },
    photo: { component: SBDynamicFormInputPhoto },
    switch: {
      component: SBDynamicFormInputSegment,
      initialValue: item.options?.[0]?.id,
    },
    optin: {
      component: SBDynamicFormInputOptin,
      valuePropName: 'checked',
      hideLabel: true,
    },
  }

  if (!(item.type.name in inputMap)) {
    console.warn(`No input component found for item type: ${item.type.name}`)
    return <></>
  }

  const inputDetails = inputMap[item.type.name as keyof typeof inputMap]
  const InputComponent = inputDetails.component
  const inputProps = { item, name: [...pathPrefix, item.name] }

  const rules: Rule[] = inputDetails.rules ?? []
  if (item.required) {
    rules.push(item.type.name === 'optin' ? acceptedRule(t) : requiredRule(t))
  }

  //Exceptions for inputs that contain their own Form.Item
  if (item.type.name === 'plan' || item.type.name === 'photo') {
    return (
      <ColWrapper item={item}>
        <InputComponent {...inputProps} />
      </ColWrapper>
    )
  }

  return (
    <ColWrapper item={item}>
      <Form.Item
        name={inputProps.name}
        label={inputDetails.hideLabel ? undefined : item.title}
        required={item.required}
        rules={rules}
        initialValue={inputDetails.initialValue}
        getValueProps={inputDetails.getValueProps}
        getValueFromEvent={inputDetails.getValueFromEvent}
        valuePropName={inputDetails.valuePropName}
      >
        <InputComponent {...inputProps} />
      </Form.Item>
    </ColWrapper>
  )
}

export default SBDynamicFormItem
